import type { Exam, ExamSchedule, ExamSession, ExamType } from "../../services/examinationService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Examinations: papers, their mark sheets, candidates, exam periods and exam
 * types. The results stages and grade bands mirror apps/examination/views.py
 * (bulk_marks, submit/moderate/approve/return_results, publish).
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");

export const STAGES: { key: Exam["results_status"]; label: string; dot: string }[] = [
  { key: "draft", label: "Marking", dot: "bg-gray-400" },
  { key: "submitted", label: "With the head of department", dot: "bg-indigo-500" },
  { key: "moderated", label: "With the head teacher", dot: "bg-sky-500" },
  { key: "approved", label: "Ready to publish", dot: "bg-amber-500" },
  { key: "published", label: "Published", dot: "bg-emerald-500" },
];
/** The stage's label and dot, for a StatusDot (without the key, which React would take as its own). */
export const stageOf = (row: Pick<Exam, "results_status">) => {
  const stage = STAGES.find((item) => item.key === row.results_status) ?? STAGES[0];
  return { label: stage.label, dot: stage.dot };
};

/** Grade bands, as bulk_marks sets them. */
const BANDS: [number, string][] = [[90, "A+"], [80, "A"], [70, "B+"], [60, "B"], [50, "C+"], [40, "C"]];
export const gradeFor = (percentage: number) => BANDS.find(([floor]) => percentage >= floor)?.[1] ?? "F";
export const GRADES = [...BANDS.map(([, grade]) => grade), "F"];

export const timeRange = (row: Pick<Exam, "start_time" | "end_time">) => (row.start_time ? `${clock(row.start_time)}–${clock(row.end_time)}` : "");

/** "12 of 30 marked", counting absences as accounted for. */
export const markedLabel = (row: Pick<Exam, "marked_count" | "candidate_count">) =>
  row.candidate_count ? `${row.marked_count} of ${row.candidate_count}` : "No candidates";

export interface Abilities {
  canModerate: boolean;
  canApprove: boolean;
  canPublish: boolean;
}

/** What the signed-in person can do with a paper's results next, if anything. */
export function nextAction(row: Pick<Exam, "results_status" | "can_mark" | "candidate_count" | "marked_count">, can: Abilities) {
  switch (row.results_status) {
    case "draft":
      if (!row.can_mark) return null;
      return row.candidate_count && row.marked_count === row.candidate_count ? "submit" : "mark";
    case "submitted":
      return can.canModerate ? "moderate" : null;
    case "moderated":
      return can.canApprove ? "approve" : null;
    case "approved":
      return can.canPublish ? "publish" : null;
    default:
      return null;
  }
}

export const ACTION_LABELS: Record<string, string> = { mark: "Enter marks", submit: "Submit", moderate: "Moderate", approve: "Approve", publish: "Publish" };

export const paperConfig = (can: Abilities): RegisterConfig<Exam> => ({
  search: (row) => [row.name, row.subject_name, row.exam_type_name, row.class_names.join(" "), row.venue, row.term_name].join(" "),
  views: [
    { key: "mine", label: "Waiting for me", match: (row) => nextAction(row, can) !== null },
    { key: "marking", label: "Being marked", match: (row) => row.results_status === "draft" },
    { key: "review", label: "In review", match: (row) => row.results_status === "submitted" || row.results_status === "moderated" },
    { key: "approved", label: "Ready to publish", match: (row) => row.results_status === "approved" },
    { key: "published", label: "Published", match: (row) => row.results_status === "published" },
    { key: "all", label: "All" },
  ],
  date: { label: "Exam date", value: (row) => row.exam_date },
  category: { label: "Subject", value: (row) => row.subject_name },
  sorts: [
    { key: "-date", label: "Latest first", compare: reversed(byText<Exam>((row) => `${row.exam_date}${row.start_time}`)) },
    { key: "date", label: "Earliest first", compare: byText<Exam>((row) => `${row.exam_date}${row.start_time}`) },
    { key: "subject", label: "Subject", compare: byText<Exam>((row) => `${row.subject_name} ${row.name}`) },
  ],
});

// --- Mark sheet ------------------------------------------------------------------------------

export interface SheetEntry {
  marks: string;
  absent: boolean;
  remarks: string;
}

export function sheetDraft(sessions: ExamSession[]): Record<number, SheetEntry> {
  return Object.fromEntries(
    sessions.map((session) => [
      session.id,
      {
        marks: session.result?.marks_obtained != null ? String(Number(session.result.marks_obtained)) : "",
        absent: Boolean(session.result?.is_absent) || session.status === "absent",
        remarks: session.result?.remarks ?? "",
      },
    ]),
  );
}

/** Problems that stop a sheet saving, by candidate; mirrors bulk_marks' 0..total check. */
export function sheetProblems(entries: Record<number, SheetEntry>, total: number) {
  const problems: Record<number, string> = {};
  for (const [id, entry] of Object.entries(entries)) {
    if (entry.absent || entry.marks.trim() === "") continue;
    const value = Number(entry.marks);
    if (!Number.isFinite(value)) problems[Number(id)] = "Not a number";
    else if (value < 0 || value > total) problems[Number(id)] = `0 to ${total}`;
  }
  return problems;
}

/** Rows to send: every candidate with a mark or marked absent. */
export const sheetRows = (entries: Record<number, SheetEntry>) =>
  Object.entries(entries)
    .filter(([, entry]) => entry.absent || entry.marks.trim() !== "")
    .map(([id, entry]) => (entry.absent ? { session_id: Number(id), absent: true, remarks: entry.remarks } : { session_id: Number(id), marks: Number(entry.marks), remarks: entry.remarks }));

/** Average, pass rate and grade counts for the marks on a sheet (absences left out). */
export function sheetSummary(entries: Record<number, SheetEntry>, total: number, passing: number) {
  const marks = Object.values(entries).filter((entry) => !entry.absent && entry.marks.trim() !== "" && Number.isFinite(Number(entry.marks))).map((entry) => Number(entry.marks));
  const grades = Object.fromEntries(GRADES.map((grade) => [grade, 0])) as Record<string, number>;
  for (const mark of marks) grades[gradeFor((mark / total) * 100)] += 1;
  return {
    marked: marks.length,
    absent: Object.values(entries).filter((entry) => entry.absent).length,
    blank: Object.values(entries).filter((entry) => !entry.absent && entry.marks.trim() === "").length,
    average: marks.length ? (marks.reduce((sum, mark) => sum + mark, 0) / marks.length / total) * 100 : null,
    passed: marks.filter((mark) => mark >= passing).length,
    grades,
  };
}

// --- Candidates, periods and types -----------------------------------------------------------

export const candidateConfig: RegisterConfig<ExamSession> = {
  search: (row) => [row.student_name, row.student_number, row.class_name, row.exam_name, row.hall, row.seat_number, row.invigilator_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "scheduled", label: "To sit", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
    { key: "done", label: "Sat", match: (row) => row.status === "completed" },
    { key: "absent", label: "Absent", match: (row) => row.status === "absent" },
  ],
  category: { label: "Paper", value: (row) => row.exam_name },
  sorts: [
    { key: "seat", label: "Hall and seat", compare: byText<ExamSession>((row) => `${row.exam_name} ${row.hall ?? ""} ${row.seat_number ?? ""}`) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<ExamSession>((row) => row.student_name) },
    { key: "class", label: "Class", compare: byText<ExamSession>((row) => `${row.class_name} ${row.student_name}`) },
  ],
};

export function periodStatus(row: Pick<ExamSchedule, "is_published" | "is_active" | "end_date">, today: string) {
  if (row.is_published) return row.end_date < today ? { label: "Done", dot: "bg-gray-400" } : { label: "Published", dot: "bg-emerald-500" };
  return row.is_active ? { label: "Timetabled, not published", dot: "bg-amber-500" } : { label: "Being planned", dot: "bg-indigo-500" };
}

export const periodConfig = (today: string): RegisterConfig<ExamSchedule> => ({
  search: (row) => [row.name, row.term_name, row.academic_year_name].join(" "),
  views: [
    { key: "current", label: "Current and coming", match: (row) => row.end_date >= today },
    { key: "past", label: "Past", match: (row) => row.end_date < today },
    { key: "all", label: "All" },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  category: { label: "Year", value: (row) => row.academic_year_name },
  sorts: [
    { key: "-start", label: "Latest first", compare: reversed(byText<ExamSchedule>((row) => row.start_date)) },
    { key: "start", label: "Earliest first", compare: byText<ExamSchedule>((row) => row.start_date) },
  ],
});

export const typeConfig: RegisterConfig<ExamType> = {
  search: (row) => [row.name, row.code, row.description].join(" "),
  views: [
    { key: "active", label: "In use", match: (row) => row.is_active },
    { key: "major", label: "Major", match: (row) => row.is_major_exam },
    { key: "retired", label: "Retired", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<ExamType>((row) => row.name) },
    { key: "-weight", label: "Weight", compare: reversed(byNumber<ExamType>((row) => Number(row.weight_percentage))) },
  ],
};

export const typeLabel = (row: Pick<Exam, "exam_type_name" | "is_major_exam">) => `${row.exam_type_name}${row.is_major_exam ? " (major)" : ""}`;
export const titleCase = humanise;
